import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import api from '../../lib/api';
import { lotPaymentsAPI } from '../../lib/lotPaymentsApi';
import { messageErreur } from '../../lib/erreurs';
import { creerGardeCle, nombreSaisi } from '../../lib/transportStatements';
import { jourDuJour } from '../../lib/lotsFiltres';
import { formatMontant, symboleDevise } from '../../lib/formats';
import { useSyncOuEnLigne } from '../../context/SyncContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { FieldError } from '../ui/field-error';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SearchableSelect } from '../ui/searchable-select';
import { toast } from '../ui/sonner';

/** Les comptes de trésorerie de la liste du serveur, quelle que soit l'enveloppe. */
const comptesDe = (reponse) => {
  const corps = reponse?.data?.data ?? reponse?.data;
  if (Array.isArray(corps)) return corps;
  return Array.isArray(corps?.items) ? corps.items : [];
};

/**
 * « Payer le reste » d'un décompte validé : un paiement au partenaire, depuis un
 * compte de trésorerie, affecté à la facture du décompte. Le montant proposé est
 * le reste ; on peut payer moins (paiement en plusieurs fois), jamais plus. La
 * même demande rejouée garde sa clé d'idempotence.
 */
export const PaiementDecompteDialog = ({
  open,
  onOpenChange,
  lotId,
  settlementId,
  reference,
  reste,
  devise = 'XOF',
  onDone,
}) => {
  const resteNombre = Math.max(0, Number(reste) || 0);
  const [form, setForm] = useState({ montant: '', compte: '', date: jourDuJour(), note: '' });
  const [comptes, setComptes] = useState([]);
  const [tentative, setTentative] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const garde = useRef(creerGardeCle('lot-decompte-paiement'));
  // L'argent attend le serveur : bouton inactif sans lui.
  const enLigne = useSyncOuEnLigne().isOnline !== false;

  useEffect(() => {
    if (!open) return undefined;
    setForm({ montant: resteNombre > 0 ? String(resteNombre) : '', compte: '', date: jourDuJour(), note: '' });
    setTentative(false);
    setEnCours(false);
    let actif = true;
    api
      .get('/finance/accounts')
      .then((reponse) => {
        if (actif) setComptes(comptesDe(reponse));
      })
      .catch(() => {
        if (actif) setComptes([]);
      });
    return () => {
      actif = false;
    };
    // Le reste proposé est celui du moment de l'ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Un compte dans une autre devise que le décompte ne peut pas le payer.
  const options = useMemo(
    () =>
      comptes
        .filter((compte) => compte?.id && (!compte.currency || compte.currency === devise))
        .map((compte) => ({
          value: compte.id,
          label: `${compte.name || 'Compte sans nom'} · ${symboleDevise(compte.currency || devise)}`,
        })),
    [comptes, devise],
  );

  const changer = (nom, valeur) => setForm((precedent) => ({ ...precedent, [nom]: valeur }));
  const montant = nombreSaisi(form.montant);
  const erreurs = {
    montant: !(montant > 0),
    depasse: montant > 0 && montant > resteNombre,
    compte: !form.compte,
  };

  const envoyer = async (evenement) => {
    evenement.preventDefault();
    if (enCours) return;
    setTentative(true);
    if (erreurs.montant || erreurs.depasse || erreurs.compte) {
      toast.error('Complétez les champs signalés');
      return;
    }
    setEnCours(true);
    try {
      const corps = {
        amount: montant,
        account_id: form.compte,
        date: form.date || undefined,
        note: form.note.trim() || undefined,
      };
      const resultat = await lotPaymentsAPI.paySettlement(
        lotId,
        settlementId,
        corps,
        garde.current({ settlementId, corps }),
      );
      // Enregistré : un second paiement identique est un nouveau paiement, pas un rejeu.
      garde.current = creerGardeCle('lot-decompte-paiement');
      toast.success('Paiement enregistré');
      onOpenChange(false);
      onDone?.(resultat);
    } catch (err) {
      toast.error(messageErreur(err, 'Enregistrement du paiement impossible'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(ouvert) => !enCours && onOpenChange(ouvert)}>
      <DialogContent className="sm:max-w-lg" data-testid="paiement-decompte-dialog">
        <DialogHeader>
          <DialogTitle>Payer le reste{reference ? ` · ${reference}` : ''}</DialogTitle>
          <DialogDescription className="sr-only">
            Montant, compte de trésorerie, date et observations du paiement.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={envoyer} className="space-y-4">
          <p className="flex items-baseline justify-between gap-3 text-sm" data-testid="paiement-decompte-reste">
            <span className="text-muted-foreground">Reste à payer</span>
            <span className="whitespace-nowrap font-semibold tabular-nums">{formatMontant(resteNombre, devise)}</span>
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="paiement-decompte-montant">Montant</Label>
            <div className="relative">
              <Input
                id="paiement-decompte-montant"
                type="number"
                inputMode="decimal"
                min="0"
                max={resteNombre}
                step="any"
                value={form.montant}
                disabled={enCours}
                onChange={(evenement) => changer('montant', evenement.target.value)}
                className="pr-16 text-right tabular-nums"
                data-testid="paiement-decompte-montant"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {symboleDevise(devise)}
              </span>
            </div>
            <FieldError show={tentative && erreurs.montant}>Saisissez un montant supérieur à zéro.</FieldError>
            <FieldError show={tentative && erreurs.depasse}>
              Au plus le reste à payer : {formatMontant(resteNombre, devise)}.
            </FieldError>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="paiement-decompte-compte">Payé depuis</Label>
            <SearchableSelect
              id="paiement-decompte-compte"
              placeholder="Choisir le compte"
              searchPlaceholder="Rechercher un compte…"
              emptyMessage="Aucun compte de trésorerie"
              value={form.compte}
              onValueChange={(valeur) => changer('compte', valeur)}
              options={options}
              disabled={enCours}
              data-testid="paiement-decompte-compte"
            />
            <FieldError show={tentative && erreurs.compte}>Choisissez le compte qui paie.</FieldError>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="paiement-decompte-date">Date</Label>
              <Input
                id="paiement-decompte-date"
                type="date"
                value={form.date}
                disabled={enCours}
                onChange={(evenement) => changer('date', evenement.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="paiement-decompte-note">Observations</Label>
              <Input
                id="paiement-decompte-note"
                value={form.note}
                maxLength={500}
                placeholder="Facultatif"
                disabled={enCours}
                onChange={(evenement) => changer('note', evenement.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={enCours}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || !enLigne} data-testid="paiement-decompte-envoyer">
              {enCours ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Enregistrer le paiement
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PaiementDecompteDialog;
